import type { ViewConfigAdditionalConfig, ViewConfigWithMetric } from '../../schemas/view_config/index.ts'
import { type CompileContext, CompileLog, type CompileResources } from './common.ts'
import { type CompiledFormViewConfig, compileFormViewConfig } from './compileViewConfig/form/compileFormViewConfig.ts'
import { type CompiledKpiViewConfig, compileKpiViewConfig } from './compileViewConfig/kpi/compileKpiViewConfig.ts'
import {
  type CompiledKpiPivotViewConfig,
  compileKpiPivotViewConfig,
} from './compileViewConfig/kpiPivot/compileKpiPivotViewConfig.ts'
import { ModelSearcher } from './compileViewConfig/ModelSearcher.ts'
import {
  type CompiledSheetViewConfig,
  compileSheetViewConfig,
} from './compileViewConfig/sheet/compileSheetViewConfig.ts'
import { ViewSearcher } from './compileViewConfig/ViewSearcher.ts'

/**
 * ViewConfig を ViewQuery と ViewUI にコンパイルする CORE のコアロジック。
 *
 * モデル情報は config に直接含めず、コンパイル時に `resources.models` を参照
 * して解決します。実行時に動的に決まる値 (分析軸の選択など) は
 * `context.additionalConfig` 経由で渡します。「何が config に含まれるか
 * (永続化される値) / 何が additionalConfig に含まれるか (実行時に動的に
 * 決まる値)」の区別は CORE 理解の要点です。
 *
 * 詳細は packages/common/core/README.md を参照。
 */
export function compileViewConfig(
  config: ViewConfigWithMetric,
  resources: CompileResources,
  context?: {
    additionalConfig?: ViewConfigAdditionalConfig
  },
): CompiledSheetViewConfig | CompiledKpiViewConfig | CompiledKpiPivotViewConfig | CompiledFormViewConfig | undefined {
  // eslint-disable-next-line sonarjs/todo-tag
  // TODO: 2022/11/11 どこかのタイミングでviewがundefinedになりうるようなので……。
  // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition,sonarjs/different-types-comparison
  if (resources.view === undefined) {
    return undefined
  }

  const modelSearcher = new ModelSearcher(resources.models, resources.organizationSetting.userGroupCategoryNames)
  const viewSearcher = new ViewSearcher(resources.views)
  const { type } = config
  const logs = new CompileLog()
  const compileContext: CompileContext = {
    resources,
    additionalConfig: context?.additionalConfig,
    modelSearcher,
    viewSearcher,
    logs,
  }

  switch (type) {
    case 'sheet': {
      return compileSheetViewConfig(config, compileContext)
    }
    case 'form': {
      return compileFormViewConfig(config, compileContext)
    }
    case 'kpi': {
      return compileKpiViewConfig(config, compileContext)
    }
    case 'kpiPivot': {
      return compileKpiPivotViewConfig(config, compileContext)
    }
  }
}

export const isKpiOrKpiPivotConfig = (
  compiledViewConfig:
    CompiledSheetViewConfig | CompiledKpiViewConfig | CompiledKpiPivotViewConfig | CompiledFormViewConfig,
): compiledViewConfig is CompiledKpiViewConfig | CompiledKpiPivotViewConfig =>
  compiledViewConfig.type === 'kpi' || compiledViewConfig.type === 'kpiPivot'
